import { GridSelection, PackageResult, PricingOrderResult, PricingRequest, PricingResponse, PromoCodeBySubject } from './types';

/**
 * One order for the school's selection. Codes join by subject, not by package, because the code
 * outlives the selection: changing stage or length gives the subject a new packagesId.
 *
 * Pending subjects are shown but not paid for, so they are priced as order 2: order 1 must total
 * what the payment will charge. With nothing left to pay, everything stays in one order.
 */
export const buildSelectionOrders = (
  selection: GridSelection,
  promoCodeBySubject: PromoCodeBySubject,
  pendingSubjectIds: number[] = []
): PricingRequest => {
  const subjectIds = Object.keys(selection)
    .map((k) => Number(k))
    .sort((a, b) => a - b);
  const payable = subjectIds.filter((id) => pendingSubjectIds.indexOf(id) === -1);
  const pending = subjectIds.filter((id) => pendingSubjectIds.indexOf(id) !== -1);
  const split = payable.length > 0 && pending.length > 0;

  const packagePromos = (ids: number[]) =>
    ids.map((subjectsId) => ({
      packagesId: selection[subjectsId],
      promoCode: promoCodeBySubject[subjectsId] || '',
    }));

  return {
    orders: split
      ? [
          { orderNumber: 1, packagePromos: packagePromos(payable) },
          { orderNumber: 2, packagePromos: packagePromos(pending) },
        ]
      : [{ orderNumber: 1, packagePromos: packagePromos(subjectIds) }],
  };
};

/** One order holding every selected package, so order-level discounts can be evaluated across them. */
export const buildBasketOrders = (packageIds: number[], promoCodes: string[] = []): PricingRequest => ({
  orders: [
    {
      orderNumber: 1,
      packagePromos: packageIds.map((packagesId, idx) => ({
        packagesId,
        promoCode: promoCodes[idx] || '',
      })),
    },
  ],
});

/** One order per candidate package, priced independently. Order N prices packageIds[N - 1]. */
export const buildCandidateOrders = (packageIds: number[]): PricingRequest => ({
  orders: packageIds.map((packagesId, idx) => ({
    orderNumber: idx + 1,
    packagePromos: [{ packagesId, promoCode: '' }],
  })),
});

/** Exactly one order is expected; anything else is a contract violation, not something to trim. */
export const pickBasketOrder = (
  response: PricingResponse | null | undefined
): { order: PricingOrderResult } | { error: string } => {
  const orders = response && Array.isArray(response.orderResults) ? response.orderResults : [];

  if (orders.length === 1) {
    return { order: orders[0] };
  }

  if (orders.length === 0) {
    return { error: 'No pricing summary data found' };
  }

  return { error: `Unexpected pricing response: expected 1 order, received ${orders.length} orders` };
};

/** The order to pay for, plus the pending order when the basket was split. */
export const pickSelectionOrders = (
  response: PricingResponse | null | undefined,
  split: boolean
): { order: PricingOrderResult; pendingOrder: PricingOrderResult | null } | { error: string } => {
  if (!split) {
    const picked = pickBasketOrder(response);
    return 'order' in picked ? { order: picked.order, pendingOrder: null } : picked;
  }

  const orders = response && Array.isArray(response.orderResults) ? response.orderResults : [];
  const order = orders.find((o) => o.orderNumber === 1);
  const pendingOrder = orders.find((o) => o.orderNumber === 2);

  if (orders.length !== 2 || !order || !pendingOrder) {
    return { error: `Unexpected pricing response: expected 2 orders, received ${orders.length} orders` };
  }

  return { order, pendingOrder };
};

/** Maps each candidate package id to the package result of the order that priced it. */
export const mapCandidatePrices = (
  response: PricingResponse | null | undefined,
  packageIds: number[]
): { [packagesId: number]: PackageResult } => {
  const prices: { [packagesId: number]: PackageResult } = {};
  const orders = response && Array.isArray(response.orderResults) ? response.orderResults : [];

  for (const order of orders) {
    const expectedPackageId = packageIds[order.orderNumber - 1];
    const pkg = (order.orderDiscountResult?.packageResults || [])[0];

    if (expectedPackageId !== undefined && pkg && pkg.packagesID === expectedPackageId) {
      prices[expectedPackageId] = pkg;
    }
  }

  return prices;
};
